import type { RgbColor } from '../color/types';
import type { Grid } from '../quantize/types';

/**
 * Charge un `File` (depuis un input file ou drop) en `HTMLImageElement`.
 * Révoque automatiquement l'URL temporaire après chargement.
 *
 * @throws si le fichier n'est pas une image valide.
 */
export function fileToImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = (): void => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (): void => {
      URL.revokeObjectURL(url);
      reject(new Error('Image invalide ou format non supporté.'));
    };
    img.src = url;
  });
}

/** Options de capture d'une image vers un Grid de pixels. */
export type ImageToGridOptions = {
  /**
   * Taille max (en px) du plus grand côté de l'image source avant capture.
   * Au-dessus, l'image est pré-redimensionnée par le canvas natif (gain CPU/mémoire
   * sur les grosses photos). Défaut : 500.
   */
  maxSourceDimension?: number;
  /**
   * Si fourni, blend les pixels transparents avec cette couleur de fond avant
   * lecture des pixels. Utile pour les sources avec canal alpha (PNG, SVG) où
   * l'utilisateur veut « remplir » les zones transparentes d'une teinte donnée
   * plutôt que d'avoir du noir (pixels RGBA = 0,0,0,0 lus comme RGB=0,0,0).
   */
  fillTransparency?: RgbColor;
};

/**
 * Extrait les pixels d'une image en `Grid`, via un canvas offscreen.
 *
 * Si la dimension max de l'image source dépasse `maxSourceDimension`, l'image est
 * pré-redimensionnée par le canvas natif (gain mémoire + CPU sur grandes photos).
 * Le resize fin vers la grille perle finale est ensuite assuré par le pipeline
 * (`imageToPattern` → `resize`) pour respecter le mode `nearest`/`bilinear` voulu.
 *
 * Avec `fillTransparency`, les pixels transparents sont rendus opaques en
 * blendant avec cette couleur (utile pour PNG/SVG).
 *
 * @throws si le contexte 2D n'est pas disponible (navigateur très ancien) ou si
 *   l'image est vide / pas encore chargée.
 */
export function imageToGrid(
  image: HTMLImageElement | HTMLCanvasElement | ImageBitmap,
  optsOrMax: ImageToGridOptions | number = {},
): Grid {
  const opts: ImageToGridOptions = typeof optsOrMax === 'number' ? { maxSourceDimension: optsOrMax } : optsOrMax;
  const maxSourceDimension = opts.maxSourceDimension ?? 500;
  const fill = opts.fillTransparency;
  const naturalW =
    image instanceof HTMLImageElement
      ? image.naturalWidth
      : 'width' in image
        ? image.width
        : 0;
  const naturalH =
    image instanceof HTMLImageElement
      ? image.naturalHeight
      : 'height' in image
        ? image.height
        : 0;
  if (naturalW === 0 || naturalH === 0) {
    throw new Error('Image vide ou pas encore chargée.');
  }

  let sw = naturalW;
  let sh = naturalH;
  const maxDim = Math.max(sw, sh);
  if (maxDim > maxSourceDimension) {
    const factor = maxSourceDimension / maxDim;
    sw = Math.max(1, Math.round(sw * factor));
    sh = Math.max(1, Math.round(sh * factor));
  }

  const canvas = document.createElement('canvas');
  canvas.width = sw;
  canvas.height = sh;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas 2D context indisponible — navigateur non supporté.');
  }
  // Pré-remplit le canvas avec la couleur de fond avant drawImage : les pixels
  // transparents de la source laisseront passer cette couleur (composite
  // source-over par défaut).
  if (fill) {
    ctx.fillStyle = `rgb(${fill.r}, ${fill.g}, ${fill.b})`;
    ctx.fillRect(0, 0, sw, sh);
  }
  ctx.drawImage(image, 0, 0, sw, sh);
  const imageData = ctx.getImageData(0, 0, sw, sh);
  const data = imageData.data;

  const pixels: RgbColor[] = new Array<RgbColor>(sw * sh);
  for (let i = 0; i < sw * sh; i++) {
    const o = i * 4;
    pixels[i] = {
      r: data[o] ?? 0,
      g: data[o + 1] ?? 0,
      b: data[o + 2] ?? 0,
    };
  }

  return { width: sw, height: sh, pixels };
}

/**
 * Rasterise l'image à `(targetW, targetH)` et retourne un masque booléen où
 * `mask[i] === true` si le pixel correspondant a une opacité < `threshold`.
 *
 * Sert au mode « transparence = cellule vide » : les zones quasi-transparentes
 * sont marquées pour devenir EMPTY après quantification. La rasterisation à
 * la dimension cible (au lieu de la source) évite de devoir threader le canal
 * alpha dans tout le pipeline (resize, quantize).
 *
 * @param threshold valeur de seuil dans [0, 255]. Pixels avec alpha < threshold
 *   sont marqués vides. Défaut : 128 (la moitié de l'opacité max).
 */
export function imageToAlphaMask(
  image: HTMLImageElement | HTMLCanvasElement | ImageBitmap,
  targetW: number,
  targetH: number,
  threshold = 128,
): boolean[] {
  if (targetW <= 0 || targetH <= 0) return [];
  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context indisponible.');
  ctx.drawImage(image, 0, 0, targetW, targetH);
  const data = ctx.getImageData(0, 0, targetW, targetH).data;
  const mask = new Array<boolean>(targetW * targetH);
  for (let i = 0; i < mask.length; i++) {
    mask[i] = (data[i * 4 + 3] ?? 255) < threshold;
  }
  return mask;
}

/**
 * Détecte rapidement si une image a au moins un pixel non-opaque.
 * Echantillonne à 64×64 max pour rester quasi-instantané même sur grosses images.
 *
 * Faux positifs possibles sur images très grandes uniquement opaques aux marges
 * (sous-échantillonnage perd un pixel transparent isolé) — acceptable, on
 * propose juste l'UI de choix.
 */
export function hasTransparency(
  image: HTMLImageElement | HTMLCanvasElement | ImageBitmap,
): boolean {
  if (typeof document === 'undefined') return false;
  const SAMPLE = 64;
  const canvas = document.createElement('canvas');
  canvas.width = SAMPLE;
  canvas.height = SAMPLE;
  const ctx = canvas.getContext('2d');
  if (!ctx) return false;
  try {
    ctx.drawImage(image, 0, 0, SAMPLE, SAMPLE);
    const data = ctx.getImageData(0, 0, SAMPLE, SAMPLE).data;
    for (let i = 3; i < data.length; i += 4) {
      if ((data[i] ?? 255) < 255) return true;
    }
  } catch {
    return false;
  }
  return false;
}

/**
 * Calcule des dimensions cibles en perles à partir d'un aspect ratio source,
 * en préservant l'aspect et en bornant à `maxBeads` sur la dimension la plus grande.
 *
 * Ex : aspect 16/9, maxBeads 80 → { width: 80, height: 45 }
 */
export function computeFitDimensions(
  aspectRatio: number,
  maxBeads: number,
): { width: number; height: number } {
  if (aspectRatio <= 0 || !Number.isFinite(aspectRatio)) {
    throw new Error(`Aspect ratio invalide : ${aspectRatio}`);
  }
  if (maxBeads <= 0 || !Number.isInteger(maxBeads)) {
    throw new Error(`maxBeads doit être un entier positif : ${maxBeads}`);
  }
  if (aspectRatio >= 1) {
    return { width: maxBeads, height: Math.max(1, Math.round(maxBeads / aspectRatio)) };
  }
  return { width: Math.max(1, Math.round(maxBeads * aspectRatio)), height: maxBeads };
}
